import {
  gapFillItemsForTopic,
  trueFalseStatementsForTopic,
  vocabularyItemsForTopic,
  discussionQuestionsForTopic,
} from './lessonMaterialSnippets';

/**
 * AI lesson outline: TipTap blocks for real exercise nodes (not just HTML).
 * Sections may include optional `contentBlocks: [{ type, attrs }]` (TipTap names).
 */

/** NOVAS exercise id → TipTap node name (see TextEditor extensions) */
export const EXERCISE_ID_TO_NODE = {
  fillinthegaps: 'fillInTheGapsExercise',
  truefalse: 'trueFalseExercise',
  worddefinition: 'wordDefinitionExercise',
  wordtranslation: 'wordTranslationExercise',
  matchinghalves: 'matchingHalves',
  abcd: 'abcdExercise',
  errorcorrection: 'errorCorrection',
  warmupdiscussion: 'warmUpDiscussionExercise',
  leadinactivities: 'leadInActivitiesExercise',
  createdialogue: 'createDialogueExercise',
  essentialvocabulary: 'essentialVocabularyExercise',
};

/**
 * @param {string} exerciseId
 * @returns {string | null}
 */
export function exerciseIdToNodeName(exerciseId) {
  const k = String(exerciseId || '')
    .toLowerCase()
    .replace(/\s+/g, '');
  return EXERCISE_ID_TO_NODE[k] || null;
}

/**
 * @param {object} block - { type?: string, exerciseType?: string, attrs?: object }
 * @param {string} cefrLevel
 * @returns {{ type: string, attrs: object } | null}
 */
export function normalizeContentBlock(block, cefrLevel = 'B1') {
  if (!block || typeof block !== 'object') return null;
  const type =
    block.type ||
    (block.exerciseType ? exerciseIdToNodeName(block.exerciseType) : null);
  if (!type) return null;
  const attrs = {
    level: cefrLevel,
    ...(block.attrs && typeof block.attrs === 'object' ? block.attrs : {}),
  };
  return { type, attrs };
}

/**
 * @param {object} section - outline section
 * @param {string} cefrLevel
 * @returns {Array<{ type: string, attrs: object }>}
 */
export function getTipTapBlocksForSection(section, cefrLevel = 'B1') {
  if (!section?.contentBlocks || !Array.isArray(section.contentBlocks)) return [];
  const out = [];
  section.contentBlocks.forEach((b) => {
    const n = normalizeContentBlock(b, cefrLevel);
    if (n) out.push(n);
  });
  return out;
}

/** Section titles that are not the lesson topic — don’t use them for “topic” wording */
function isGenericSectionTitle(t) {
  const s = String(t || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!s) return true;
  return /^(lead-?in|warm-?up|wrap-?up|practice|games|homework|vocabulary|grammar|reading|listening|speaking|writing|introduction|overview|main|section|production|feedback|review|consolidation|extension)(\s|$)/i.test(
    s
  );
}

/**
 * Human phrase for the lesson theme (user title), not “Lead-in”.
 * @param {string} lessonTitle
 * @param {object} [section]
 */
export function lessonTopicPhrase(lessonTitle, section) {
  const lt = String(lessonTitle || '').trim();
  if (lt) return lt;
  const obj = String(section?.objective || '').trim();
  if (obj.length > 12) return obj.slice(0, 140).replace(/\s+/g, ' ').trim();
  const st = String(section?.title || '').trim();
  if (st && !isGenericSectionTitle(st)) return st;
  return 'this lesson';
}

function syntheticFillInTheGaps(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const secLabel = String(section?.title || 'Section').trim() || 'Section';
  const sentences = gapFillItemsForTopic(topic);
  return normalizeContentBlock(
    {
      type: 'fillInTheGapsExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        sentences,
      },
    },
    cefrLevel
  );
}

function syntheticTrueFalse(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const secLabel = String(section?.title || 'Check').trim() || 'Check';
  return normalizeContentBlock(
    {
      type: 'trueFalseExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        statements: trueFalseStatementsForTopic(topic),
      },
    },
    cefrLevel
  );
}

function syntheticWarmUpDiscussion(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  return normalizeContentBlock(
    {
      type: 'warmUpDiscussionExercise',
      attrs: {
        title: `Discussion — «${topic}»`,
        questions: discussionQuestionsForTopic(topic),
      },
    },
    cefrLevel
  );
}

function syntheticLeadInActivities(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const obj = String(section?.objective || '').trim();
  const desc =
    obj ||
    `You think and speak about «${topic}» before you read or listen (${cefrLevel}).`;
  const secLabel = String(section?.title || 'Lead-in').trim() || 'Lead-in';
  return normalizeContentBlock(
    {
      type: 'leadInActivitiesExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        activities: [
          {
            type: 'discussion',
            title: `Ideas: ${topic}`,
            description: desc,
            duration: '5–7 minutes',
            materials: 'Paper or notes',
            instructions: [
              `Write 5 words or short phrases you connect with «${topic}».`,
              `In pairs: compare lists. Circle 3 items you both wrote.`,
              `Choose one item and say one full English sentence aloud.`,
            ],
            expectedOutcome: `You have a short list of English phrases you can reuse when you talk about «${topic}».`,
          },
        ],
      },
    },
    cefrLevel
  );
}

function syntheticWordDefinition(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const pairs = vocabularyItemsForTopic(topic, 6);
  const secLabel = String(section?.title || 'Vocabulary').trim() || 'Vocabulary';
  return normalizeContentBlock(
    {
      type: 'wordDefinitionExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        pairs,
      },
    },
    cefrLevel
  );
}

function syntheticWordTranslation(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const pairs = vocabularyItemsForTopic(topic, 5).map(({ word, definition }) => ({
    word,
    translation: `L1: same meaning as — ${definition}`,
  }));
  const secLabel = String(section?.title || 'Lexis').trim() || 'Lexis';
  return normalizeContentBlock(
    {
      type: 'wordTranslationExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        targetLanguage: 'L1',
        pairs,
      },
    },
    cefrLevel
  );
}

function syntheticABCD(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const secLabel = String(section?.title || 'Quiz').trim() || 'Quiz';
  return normalizeContentBlock(
    {
      type: 'abcdExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        questions: [
          {
            id: '1',
            question: `What is the main idea connected to «${topic}»?`,
            options: [
              { id: 'a', text: 'It relates to everyday life', isCorrect: true },
              { id: 'b', text: 'It is a historical concept only', isCorrect: false },
              { id: 'c', text: 'It has no practical applications', isCorrect: false },
              { id: 'd', text: 'It was discovered very recently', isCorrect: false },
            ],
          },
          {
            id: '2',
            question: `Which word best describes «${topic}»?`,
            options: [
              { id: 'a', text: 'Complex and fascinating', isCorrect: true },
              { id: 'b', text: 'Simple and irrelevant', isCorrect: false },
              { id: 'c', text: 'Old-fashioned', isCorrect: false },
              { id: 'd', text: 'Controversial only', isCorrect: false },
            ],
          },
        ],
      },
    },
    cefrLevel
  );
}

function syntheticErrorCorrection(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const secLabel = String(section?.title || 'Error Correction').trim() || 'Error Correction';
  return normalizeContentBlock(
    {
      type: 'errorCorrection',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        sentences: [
          { id: 'ec-1', text: `People is talking about ${topic} every day.`, correctAnswers: [`People are talking about ${topic} every day.`] },
          { id: 'ec-2', text: `She have learned a lot about ${topic} recently.`, correctAnswers: [`She has learned a lot about ${topic} recently.`] },
          { id: 'ec-3', text: `They was discussing ${topic} when I arrived.`, correctAnswers: [`They were discussing ${topic} when I arrived.`] },
          { id: 'ec-4', text: `He don't understand the importance of ${topic}.`, correctAnswers: [`He doesn't understand the importance of ${topic}.`] },
        ],
      },
    },
    cefrLevel
  );
}

function syntheticMatchingHalves(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const secLabel = String(section?.title || 'Matching').trim() || 'Matching';
  const vocabPairs = vocabularyItemsForTopic(topic, 4);
  const sentences = vocabPairs.length >= 2
    ? vocabPairs.map(({ word, definition }) => ({ left: word, right: definition }))
    : [
        { left: `The topic of ${topic}`, right: 'is widely discussed today.' },
        { left: `Understanding ${topic}`, right: 'helps us make better decisions.' },
        { left: `Experts who study ${topic}`, right: 'often publish research papers.' },
        { left: `Many people learn about ${topic}`, right: 'through personal experience.' },
      ];
  return normalizeContentBlock(
    {
      type: 'matchingHalves',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        sentences,
      },
    },
    cefrLevel
  );
}

function syntheticEssentialVocabulary(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const secLabel = String(section?.title || 'Vocabulary').trim() || 'Vocabulary';
  const pairs = vocabularyItemsForTopic(topic, 6);
  return normalizeContentBlock(
    {
      type: 'essentialVocabularyExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        vocabulary: pairs.map(({ word, definition }) => ({
          type: 'word',
          word,
          definition,
        })),
      },
    },
    cefrLevel
  );
}

function syntheticCreateDialogue(section, cefrLevel, lessonTitle) {
  const topic = lessonTopicPhrase(lessonTitle, section);
  const secLabel = String(section?.title || 'Dialogue').trim() || 'Dialogue';
  return normalizeContentBlock(
    {
      type: 'createDialogueExercise',
      attrs: {
        title: `${secLabel} — «${topic}»`,
        dialogue: `A: What do you think about ${topic}?\nB: I think it's really interesting. Have you read much about it?\nA: Yes, quite a bit. I find it fascinating.\nB: Me too. What aspect interests you most?`,
      },
    },
    cefrLevel
  );
}

/**
 * Trust AI-generated discussion/activity content. The previous version replaced
 * blocks whose questions did not contain the lesson topic word, which silently
 * overwrote good AI output (e.g. "What surprised you in the article?") with a
 * tiny pool of templated questions. Call B prompts now ground every exercise
 * in the lesson text, so enrichment is no longer needed.
 * @param {Array<{ type: string, attrs: object }>} blocks
 */
function enrichModelBlocksWithLessonTopic(blocks /*, section, lessonTitle, cefrLevel */) {
  return blocks;
}

/**
 * When the model omits `contentBlocks`, build concrete TipTap exercise nodes from
 * `suggestedExerciseTypes` / blockType so every section still gets widgets.
 * @param {object} section
 * @param {string} cefrLevel
 * @param {{ lessonTitle?: string }} [context] — user lesson title so questions/gaps match the theme (not “Lead-in”).
 * @returns {Array<{ type: string, attrs: object }>}
 */
export function getTipTapBlocksForSectionWithFallback(section, cefrLevel = 'B1', context = {}) {
  const lessonTitle = String(context.lessonTitle || '').trim();

  const fromModel = getTipTapBlocksForSection(section, cefrLevel);
  if (fromModel.length > 0) {
    return enrichModelBlocksWithLessonTopic(fromModel, section, lessonTitle, cefrLevel);
  }

  const types = (section?.suggestedExerciseTypes || [])
    .map((x) => String(x || '').toLowerCase().replace(/\s+/g, ''))
    .filter(Boolean);

  const blocks = [];
  const seen = new Set();

  const pushSynth = (exId) => {
    if (seen.has(exId) || blocks.length >= 3) return;
    let n = null;
    switch (exId) {
      case 'fillinthegaps':
        n = syntheticFillInTheGaps(section, cefrLevel, lessonTitle);
        break;
      case 'truefalse':
        n = syntheticTrueFalse(section, cefrLevel, lessonTitle);
        break;
      case 'warmupdiscussion':
        n = syntheticWarmUpDiscussion(section, cefrLevel, lessonTitle);
        break;
      case 'leadinactivities':
        n = syntheticLeadInActivities(section, cefrLevel, lessonTitle);
        break;
      case 'worddefinition':
        n = syntheticWordDefinition(section, cefrLevel, lessonTitle);
        break;
      case 'wordtranslation':
        n = syntheticWordTranslation(section, cefrLevel, lessonTitle);
        break;
      case 'matchinghalves':
        n = syntheticMatchingHalves(section, cefrLevel, lessonTitle);
        break;
      case 'essentialvocabulary':
        n = syntheticEssentialVocabulary(section, cefrLevel, lessonTitle);
        break;
      case 'abcd':
        n = syntheticABCD(section, cefrLevel, lessonTitle);
        break;
      case 'errorcorrection':
        n = syntheticErrorCorrection(section, cefrLevel, lessonTitle);
        break;
      case 'createdialogue':
        n = syntheticCreateDialogue(section, cefrLevel, lessonTitle);
        break;
      default:
        break;
    }
    if (n) {
      blocks.push(n);
      seen.add(exId);
    }
  };

  for (const t of types) {
    if (blocks.length >= 3) break;
    if (EXERCISE_ID_TO_NODE[t]) pushSynth(t);
  }

  if (blocks.length === 1) {
    const have = new Set(blocks.map((b) => b.type));
    if (!have.has('fillInTheGapsExercise')) {
      const f = syntheticFillInTheGaps(section, cefrLevel, lessonTitle);
      if (f) blocks.push(f);
    } else if (!have.has('trueFalseExercise')) {
      const t = syntheticTrueFalse(section, cefrLevel, lessonTitle);
      if (t) blocks.push(t);
    }
  }

  if (blocks.length === 0) {
    const bt = section?.blockType || 'doc';
    if (bt === 'main') {
      const a = syntheticWarmUpDiscussion(section, cefrLevel, lessonTitle);
      const b = syntheticFillInTheGaps(section, cefrLevel, lessonTitle);
      if (a) blocks.push(a);
      if (b) blocks.push(b);
    } else if (bt === 'homework') {
      const a = syntheticTrueFalse(section, cefrLevel, lessonTitle);
      const b = syntheticFillInTheGaps(section, cefrLevel, lessonTitle);
      if (a) blocks.push(a);
      if (b) blocks.push(b);
    } else {
      const a = syntheticFillInTheGaps(section, cefrLevel, lessonTitle);
      const b = syntheticTrueFalse(section, cefrLevel, lessonTitle);
      if (a) blocks.push(a);
      if (b) blocks.push(b);
    }
  }

  return blocks;
}

/**
 * Generate a single synthetic block for a given exercise id.
 * Used by the preview UI when the teacher changes an exercise type.
 * @param {string} exId - exercise id (e.g. 'fillinthegaps', 'truefalse')
 * @param {object} section
 * @param {string} cefrLevel
 * @param {string} lessonTitle
 * @returns {{ type: string, attrs: object } | null}
 */
export function syntheticBlockForExId(exId, section, cefrLevel = 'B1', lessonTitle = '') {
  const k = String(exId || '').toLowerCase().replace(/\s+/g, '');
  switch (k) {
    case 'fillinthegaps':    return syntheticFillInTheGaps(section, cefrLevel, lessonTitle);
    case 'truefalse':        return syntheticTrueFalse(section, cefrLevel, lessonTitle);
    case 'warmupdiscussion': return syntheticWarmUpDiscussion(section, cefrLevel, lessonTitle);
    case 'leadinactivities': return syntheticLeadInActivities(section, cefrLevel, lessonTitle);
    case 'worddefinition':   return syntheticWordDefinition(section, cefrLevel, lessonTitle);
    case 'wordtranslation':  return syntheticWordTranslation(section, cefrLevel, lessonTitle);
    case 'abcd':                return syntheticABCD(section, cefrLevel, lessonTitle);
    case 'matchinghalves':      return syntheticMatchingHalves(section, cefrLevel, lessonTitle);
    case 'errorcorrection':     return syntheticErrorCorrection(section, cefrLevel, lessonTitle);
    case 'createdialogue':      return syntheticCreateDialogue(section, cefrLevel, lessonTitle);
    case 'essentialvocabulary': return syntheticEssentialVocabulary(section, cefrLevel, lessonTitle);
    default:                    return syntheticFillInTheGaps(section, cefrLevel, lessonTitle);
  }
}

/**
 * @param {Map} map - allEditors Map
 * @param {string|number} editorId
 */
export function getEditorFromMap(map, editorId) {
  if (!map) return null;
  return map.get(editorId) ?? map.get(String(editorId)) ?? map.get(Number(editorId));
}

/**
 * Insert blocks at end of document. Retries until editor exists (post-render).
 * @param {() => import('@tiptap/core').Editor | null | undefined} getEditor
 * @param {Array<{ type: string, attrs: object }>} blocks
 * @param {{ maxAttempts?: number, delayMs?: number }} opts
 */
export function insertBlocksAtEndWithRetry(getEditor, blocks, opts = {}) {
  if (!blocks?.length) return;
  const maxAttempts = opts.maxAttempts ?? 45;
  const delayMs = opts.delayMs ?? 80;
  let attempt = 0;
  const run = () => {
    const editor = getEditor();
    if (editor && typeof editor.chain === 'function') {
      try {
        editor
          .chain()
          .focus('end')
          .insertContent(blocks.map((b) => ({ type: b.type, attrs: b.attrs })))
          .run();
      } catch (e) {
        console.warn('[lessonAiContentBlocks] insert failed', e);
      }
      return;
    }
    attempt += 1;
    if (attempt < maxAttempts) {
      setTimeout(run, delayMs);
    }
  };
  setTimeout(run, delayMs);
}
